import { useState, useEffect, useRef } from "react";
import { Megaphone, Loader2, Send, CheckCircle2, Clock, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const SEGMENTS = [
  { id: "all", label: "Todos" },
  { id: "no_balance", label: "Sem saldo/créditos" },
  { id: "resellers", label: "Revendedores" },
  { id: "inactive", label: "Inativos (sem ativações)" },
];

export default function AdminBroadcast() {
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [buttonUrl, setButtonUrl] = useState("");
  const [segment, setSegment] = useState("all");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const timer = useRef(null);

  const load = () => api.get("/admin/broadcasts").then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    load();
    timer.current = setInterval(load, 3000);
    return () => clearInterval(timer.current);
  }, []);

  const send = async () => {
    if (message.trim().length < 2) { toast.error("Escreva a mensagem do disparo"); return; }
    setSending(true);
    try {
      const payload = { message: message.trim(), image_url: imageUrl.trim() || null, segment };
      if (scheduledAt) payload.scheduled_at = new Date(scheduledAt).toISOString();
      if (buttonText.trim() && buttonUrl.trim()) { payload.button_text = buttonText.trim(); payload.button_url = buttonUrl.trim(); }
      const { data } = await api.post("/admin/broadcast", payload);
      toast.success(data.status === "scheduled"
        ? `Agendado para ${data.total} usuários`
        : `Disparo iniciado para ${data.total} usuários`);
      setMessage(""); setImageUrl(""); setScheduledAt(""); setButtonText(""); setButtonUrl("");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const segLabel = (id) => (SEGMENTS.find((s) => s.id === id) || {}).label || id;

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-broadcast">
      <div>
        <h2 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <Megaphone className="h-6 w-6 text-primary" /> Disparos no Bot
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Envie avisos ou promoções por segmento, agora ou agendado. Suporta HTML básico
          (<code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;a&gt;</code>).
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="space-y-2">
          <Label>Mensagem</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={5}
            placeholder="🎉 Promoção VULPIX! Créditos com 20% OFF só hoje..." data-testid="broadcast-message" />
        </div>

        <div className="space-y-2">
          <Label>Público-alvo</Label>
          <div className="flex flex-wrap gap-2">
            {SEGMENTS.map((s) => (
              <button key={s.id} type="button" onClick={() => setSegment(s.id)}
                data-testid={`segment-${s.id}`}
                className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${segment === s.id ? "border-primary bg-primary/15 text-primary font-medium" : "border-border hover:bg-muted/60"}`}>
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>URL da imagem (opcional)</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." data-testid="broadcast-image" />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" /> Agendar (opcional)</Label>
            <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="broadcast-schedule" />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Botão — texto (opcional)</Label>
            <Input value={buttonText} onChange={(e) => setButtonText(e.target.value)} placeholder="🚀 Ativar agora" data-testid="broadcast-btn-text" />
          </div>
          <div className="space-y-2">
            <Label>Botão — link/deep link (opcional)</Label>
            <Input value={buttonUrl} onChange={(e) => setButtonUrl(e.target.value)} placeholder="https://t.me/seubot?start=app_..." data-testid="broadcast-btn-url" />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={send} disabled={sending} data-testid="broadcast-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {scheduledAt ? " Agendar" : " Disparar"}
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-heading font-semibold">Histórico</h3>
        {history.length === 0 && <p className="text-sm text-muted-foreground">Nenhum disparo ainda.</p>}
        {history.map((b) => (
          <div key={b.id} className="rounded-lg border border-border bg-card p-4" data-testid={`broadcast-row-${b.id}`}>
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm line-clamp-2 flex-1">{b.message}</p>
              {b.status === "done"
                ? <Badge className="gap-1 border-green-500/30 bg-green-500/15 text-green-600 dark:text-green-400" variant="outline"><CheckCircle2 className="h-3 w-3" /> Concluído</Badge>
                : b.status === "scheduled"
                ? <Badge className="gap-1" variant="outline"><CalendarClock className="h-3 w-3" /> Agendado</Badge>
                : <Badge className="gap-1" variant="secondary"><Clock className="h-3 w-3 animate-pulse" /> Enviando</Badge>}
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
              <span className="text-foreground/70">🎯 {segLabel(b.segment)}</span>
              <span>✅ {b.sent} enviados</span>
              <span>⚠️ {b.failed} falhas</span>
              <span>👥 {b.total} alvo</span>
              {b.scheduled_at && <span>🕒 {new Date(b.scheduled_at).toLocaleString("pt-BR")}</span>}
              <span>{new Date(b.created_at).toLocaleString("pt-BR")}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
